import { useEffect, useRef, useState } from "react";

import { Column } from "@/components/Column";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { useRouteCandidacyThroughStevie } from "@/hooks/createAdds/useRouteCandidacyThroughStevie";
import { useIsUserInAllowedList } from "@/hooks/useIsUserInAllowedList";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { HttpError, StevieRouteMode } from "@/types";

// The 409 body carries the staffer-facing reason the route was refused; axios's
// own message is just the status code.
const describeError = (error: Error) =>
  (error as HttpError<{ message?: string }>).response?.data.message ??
  error.message;

// Retell takes this as a call variable, so it stays short enough to sit inside
// the agent's prompt without crowding it.
const STEVIE_INFO_TO_GATHER_MAX_LENGTH = 500;

// Confirms the text before it goes out — it's sent from the staffer's own Quo
// line under their name, and each candidacy only gets one.
export const RouteThroughStevieDialog = ({
  candidacyId,
  candidateFirstName,
  mode,
  onClose,
  onRouted,
}: {
  candidacyId: string;
  candidateFirstName: string;
  mode: StevieRouteMode;
  onClose: () => void;
  // Fires only when the text actually went out — cancel and failure don't.
  onRouted?: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: routeThroughStevie, isPending } =
    useRouteCandidacyThroughStevie();

  /* Context */
  // Free-text instructions land in Stevie's call prompt verbatim, so only the
  // product team can write them for now.
  const canGiveStevieInstructions = useIsUserInAllowedList();

  /* React Hooks */
  const [infoToGather, setInfoToGather] = useState("");
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  /* Helpers */
  const isRenudge = mode === StevieRouteMode.RENUDGE;

  const description = isRenudge
    ? `We'll text ${candidateFirstName} from your number pointing them back at Stevie's text so they take the call.`
    : `We'll text ${candidateFirstName} from your number, and Stevie will text them right after to book a call.`;

  /* Handlers */
  const sendRoute = () => {
    routeThroughStevie(
      { candidacyId, infoToGather: infoToGather.trim() || undefined },
      {
        onSuccess: () => {
          successToast({
            description: isRenudge
              ? `Nudged ${candidateFirstName} to take their Stevie call.`
              : `Sent ${candidateFirstName} to Stevie.`,
          });
          onRouted?.();
          onClose();
        },
        // Stay open: the staffer keeps what they typed and can retry.
        onError: (error) => {
          errorToast({ description: describeError(error) });
        },
      },
    );
  };

  const handleSend = () => {
    // The button is disabled while sending; the keyboard path isn't.
    if (isPending) return;

    mixpanelUtils.trackButtonClick(
      isRenudge ? "Nudge Stevie Call" : "Route Candidacy Through Stevie",
      MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
    );

    sendRoute();
  };

  // Cmd+Enter sends, matching the reject flow. Held in a ref so the listener
  // attaches once instead of re-binding on every pending/toggle change.
  const handleSendRef = useRef(handleSend);
  handleSendRef.current = handleSend;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey || event.key !== "Enter") return;
      event.preventDefault();
      mixpanelUtils.trackKeyboardShortcutUsed(
        "Cmd+Enter (Route Through Stevie)",
        MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
      );
      handleSendRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /* Render */
  const render = () => (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex flex-col gap-8">
        <DialogHeader className="flex flex-col gap-4">
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            {isRenudge ? "Nudge Stevie call?" : "Route through Stevie?"}
          </DialogTitle>
          <DialogDescription className="!m-0 text-base font-medium text-slate-700">
            {description} You can only do this once per candidacy.
          </DialogDescription>
        </DialogHeader>
        {canGiveStevieInstructions ? (
          <Column className="gap-2">
            <Label className="text-sm font-semibold text-slate-800">
              Anything specific you want Stevie to find out?
            </Label>
            <Textarea
              className="min-h-24 resize-none"
              maxLength={STEVIE_INFO_TO_GATHER_MAX_LENGTH}
              onChange={(event) => {
                setInfoToGather(event.target.value);
              }}
              placeholder="Optional. e.g. whether they've run a 5-axis mill, or why they left their last role."
              value={infoToGather}
            />
          </Column>
        ) : null}
        <DialogFooter>
          <Button
            className="rounded-full"
            disabled={isPending}
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full"
            disabled={isPending}
            onClick={handleSend}
          >
            {isPending ? (
              <LoadingIndicator gearColor="white" variant="small" />
            ) : (
              <span>Send text</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return render();
};
